import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import { debounce } from '../utils/debounce';
import SearchIcon from '@mui/icons-material/Search';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import OpacityIcon from '@mui/icons-material/Opacity';
import InfoIcon from '@mui/icons-material/Info';
import { offlineApiService } from '../services/offlineApi';
import toast from 'react-hot-toast';

interface PlantOption {
  _id: string;
  commonName: string;
  scientificName: string;
  plantType: string;
  images: Array<{ url: string; type: string }>;
  simplified: {
    difficulty: string;
    isEdible: boolean;
    indoorSuitable: boolean;
    outdoorSuitable: boolean;
  };
  growingRequirements: {
    sunlight: string[];
    water: string;
  };
  source: string;
  externalId: string;
}

interface PlantSearchProps {
  onSelectPlant: (plant: PlantOption) => void;
  placeholder?: string;
  label?: string;
  filters?: {
    plantType?: string;
    difficulty?: string;
    isEdible?: boolean;
    indoorOnly?: boolean;
  };
  showQuickPicks?: boolean;
}

const PlantSearch: React.FC<PlantSearchProps> = ({
  onSelectPlant,
  placeholder = "Search for plants by name...",
  label = "Search Plants",
  filters = {},
  showQuickPicks = true
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<PlantOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState<PlantOption | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [plantDetails, setPlantDetails] = useState<any>(null);
  const [popularPlants, setPopularPlants] = useState<PlantOption[]>([]);
  const [beginnerPlants, setBeginnerPlants] = useState<PlantOption[]>([]);

  // Fetch popular and beginner plants on mount
  useEffect(() => {
    if (showQuickPicks) {
      fetchQuickPicks();
    }
  }, [showQuickPicks]);

  const fetchQuickPicks = async () => {
    try {
      const [popular, beginner] = await Promise.all([
        offlineApiService.get('/plant-database/popular', { params: { limit: 6 } }),
        offlineApiService.get('/plant-database/beginner-friendly', { 
          params: { limit: 6, indoorOnly: filters.indoorOnly } 
        })
      ]);
      setPopularPlants(popular.data);
      setBeginnerPlants(beginner.data);
    } catch (error) {
      console.error('Error fetching quick picks:', error);
    }
  };

  // Debounced search function
  const searchPlants = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await offlineApiService.get('/plant-database/search', {
          params: {
            q: query,
            ...filters,
            limit: 10
          }
        });
        setOptions(response.data.plants);
      } catch (error) {
        console.error('Error searching plants:', error);
        toast.error('Failed to search plants');
      } finally {
        setLoading(false);
      }
    }, 300),
    [filters]
  );

  useEffect(() => {
    searchPlants(inputValue);
  }, [inputValue, searchPlants]);

  const handleSelectPlant = async (plant: PlantOption | null) => {
    if (!plant) return;
    
    setSelectedPlant(plant);
    
    // Fetch full details
    try {
      const response = await offlineApiService.get(
        `/plant-database/${plant.source}/${plant.externalId}`
      );
      setPlantDetails(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching plant details:', error);
      // Still allow selection even if details fail
      onSelectPlant(plant);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedPlant) {
      onSelectPlant(selectedPlant);
      setDetailsOpen(false);
      setInputValue('');
      setSelectedPlant(null);
    }
  };

  const getSunlightIcon = (sunlight: string) => {
    switch (sunlight) {
      case 'full-sun':
        return <WbSunnyIcon sx={{ color: 'orange' }} />;
      case 'partial-sun':
      case 'partial-shade':
        return <WbSunnyIcon sx={{ color: 'gold' }} />;
      case 'full-shade':
        return <WbSunnyIcon sx={{ color: 'grey.500' }} />;
      default:
        return <WbSunnyIcon />;
    }
  };

  const getWaterIcon = (water: string) => {
    const colors: { [key: string]: string } = {
      low: 'grey.400',
      moderate: 'blue.400',
      high: 'blue.600',
      'very-high': 'blue.800'
    };
    return <OpacityIcon sx={{ color: colors[water] || 'blue.500' }} />;
  };

  return (
    <>
      <Box>
        <Autocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={options}
          loading={loading}
          inputValue={inputValue}
          onInputChange={(_, value) => setInputValue(value)}
          onChange={(_, value) => handleSelectPlant(value)}
          getOptionLabel={(option) => option.commonName}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Avatar
                src={option.images[0]?.url}
                sx={{ mr: 2, width: 40, height: 40 }}
              >
                <LocalFloristIcon />
              </Avatar>
              <Box flex={1}>
                <Typography variant="body1">{option.commonName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.scientificName}
                </Typography>
                <Box display="flex" gap={0.5} mt={0.5}>
                  <Chip
                    label={option.plantType}
                    size="small"
                    variant="outlined"
                  />
                  {option.simplified.isEdible && (
                    <Chip label="Edible" size="small" color="success" />
                  )}
                  <Chip
                    label={option.simplified.difficulty}
                    size="small"
                    color={
                      option.simplified.difficulty === 'beginner'
                        ? 'success'
                        : option.simplified.difficulty === 'intermediate'
                        ? 'warning'
                        : 'error'
                    }
                  />
                </Box>
              </Box>
              <Box display="flex" gap={1}>
                {getSunlightIcon(option.growingRequirements.sunlight[0])}
                {getWaterIcon(option.growingRequirements.water)}
              </Box>
            </Box>
          )}
          noOptionsText={
            inputValue.length < 2
              ? "Type at least 2 characters to search"
              : "No plants found"
          }
        />

        {/* Quick Picks */}
        {showQuickPicks && !inputValue && (
          <Box mt={3}>
            {/* Popular Plants */}
            {popularPlants.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Popular Plants
                </Typography>
                <Grid container spacing={1}>
                  {popularPlants.map(plant => (
                    <Grid item xs={6} sm={4} key={plant._id}>
                      <Paper
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleSelectPlant(plant)}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={plant.images[0]?.url}
                            sx={{ width: 30, height: 30 }}
                          >
                            <LocalFloristIcon fontSize="small" />
                          </Avatar>
                          <Box flex={1} overflow="hidden">
                            <Typography variant="body2" noWrap>
                              {plant.commonName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {plant.plantType}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Beginner-Friendly Plants */}
            {beginnerPlants.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Great for Beginners
                </Typography>
                <Grid container spacing={1}>
                  {beginnerPlants.map(plant => (
                    <Grid item xs={6} sm={4} key={plant._id}>
                      <Paper
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleSelectPlant(plant)}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={plant.images[0]?.url}
                            sx={{ width: 30, height: 30 }}
                          >
                            <LocalFloristIcon fontSize="small" />
                          </Avatar>
                          <Box flex={1} overflow="hidden">
                            <Typography variant="body2" noWrap>
                              {plant.commonName}
                            </Typography>
                            <Box display="flex" gap={0.5}>
                              <Chip
                                label="Easy"
                                size="small"
                                color="success"
                                sx={{ height: 16, fontSize: '0.7rem' }}
                              />
                              {plant.simplified.indoorSuitable && (
                                <Chip
                                  label="Indoor"
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Plant Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={plantDetails?.images[0]?.url}
              sx={{ width: 60, height: 60 }}
            >
              <LocalFloristIcon />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6">{plantDetails?.commonName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {plantDetails?.scientificName}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {plantDetails && (
            <>
              {/* Basic Info */}
              <Box mb={2}>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip label={plantDetails.plantType} />
                  {plantDetails.lifecycle && <Chip label={plantDetails.lifecycle} />}
                  {plantDetails.simplified.isEdible && (
                    <Chip label="Edible" color="success" />
                  )}
                  <Chip
                    label={`${plantDetails.simplified.difficulty} level`}
                    color={
                      plantDetails.simplified.difficulty === 'beginner'
                        ? 'success'
                        : plantDetails.simplified.difficulty === 'intermediate'
                        ? 'warning'
                        : 'error'
                    }
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Growing Requirements */}
              <Typography variant="subtitle2" gutterBottom>
                Growing Requirements
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'transparent' }}>
                      <WbSunnyIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Sunlight"
                    secondary={plantDetails.growingRequirements.sunlight.join(', ')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'transparent' }}>
                      <OpacityIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Water"
                    secondary={plantDetails.growingRequirements.water}
                  />
                </ListItem>
                {plantDetails.growingRequirements.soilPH && (
                  <ListItem>
                    <ListItemText
                      primary="Soil pH"
                      secondary={`${plantDetails.growingRequirements.soilPH.min} - ${plantDetails.growingRequirements.soilPH.max}`}
                      inset
                    />
                  </ListItem>
                )}
                {plantDetails.growingRequirements.hardiness && (
                  <ListItem>
                    <ListItemText
                      primary="Hardiness Zones"
                      secondary={`${plantDetails.growingRequirements.hardiness.min} - ${plantDetails.growingRequirements.hardiness.max}`}
                      inset
                    />
                  </ListItem>
                )}
              </List>

              {/* Growth Info */}
              {plantDetails.growth && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Growth Information
                  </Typography>
                  <Grid container spacing={2}>
                    {plantDetails.growth.height && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Height
                        </Typography>
                        <Typography variant="body1">
                          {plantDetails.growth.height.min || 0} - {plantDetails.growth.height.max} {plantDetails.growth.height.unit}
                        </Typography>
                      </Grid>
                    )}
                    {plantDetails.growth.spread && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Spread
                        </Typography>
                        <Typography variant="body1">
                          {plantDetails.growth.spread.min || 0} - {plantDetails.growth.spread.max} {plantDetails.growth.spread.unit}
                        </Typography>
                      </Grid>
                    )}
                    {plantDetails.growth.daysToMaturity && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Days to Maturity
                        </Typography>
                        <Typography variant="body1">
                          {plantDetails.growth.daysToMaturity.min} - {plantDetails.growth.daysToMaturity.max} days
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* Planting Info */}
              {plantDetails.planting?.companionPlants?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Companion Plants
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {plantDetails.planting.companionPlants.map((companion: string) => (
                      <Chip key={companion} label={companion} size="small" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSelection} variant="contained">
            Select This Plant
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlantSearch;